import { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Modal } from '../modal';
import { Button } from '../button';
import { useMutation } from '@apollo/client';
import { RESET_PASSWORD } from '../../services/authService/queries';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import {
  IConfirmForgotPasswordMutation,
  IConfirmForgotPasswordMutationVariables,
} from '../../services/authService/__generated__/queries.generated';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { modalActions, modalStore } from '../../../store/authStore/modalStore';
import { useSnapshot } from 'valtio';

const ModalContainer = styled.div`
  ${tw`flex flex-col items-center justify-center w-full`}
`;

const FormGroup = styled.div`
  ${tw`mb-4 w-full`}
`;

const Container = styled.div`
  ${tw`flex flex-col gap-3 w-full max-w-md`}
`;

const Title = styled.h2`
  ${tw`text-xl md:text-2xl font-bold mb-2`}
`;

const Subtitle = styled.p`
  ${tw`text-text-secondary text-sm mb-4`}
`;

const Input = styled.input`
  ${tw`border border-solid border-border-default rounded-md w-[100%] relative h-[44px] mt-2`}
`;

const Label = styled.label`
  ${tw`text-sm text-text-secondary`}
`;

const TextContainer = styled.div`
  ${tw`flex flex-col gap-1`}
`;

const PasswordInputWrapper = styled.div`
  ${tw`relative`}
`;

const EyeIcon = styled.button`
  ${tw`absolute right-0 top-1/3 -translate-y-1/2 text-text-secondary`}
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    ${tw`text-text-secondary`}
  }
`;

const ValidationMessage = styled.p`
  ${tw`text-red-500 text-sm mt-1`}
`;

const PasswordRequirements = styled.div`
  ${tw`mt-2 space-y-1`}
`;

const RequirementRow = styled.div`
  ${tw`flex items-center`}
`;

const RequirementIcon = styled.span<{ isValid: boolean }>`
  ${tw`w-5 text-center text-sm`}
  color: ${props => (props.isValid ? '#34C759' : 'var(--color-text-tertiary)')};
`;

const RequirementText = styled.span<{ isValid: boolean }>`
  ${tw`text-xs ml-2`}
  color: ${props => (props.isValid ? 'var(--color-text-secondary)' : 'var(--color-text-tertiary)')};
`;

interface ResetPwdModalProps {
  isOpen: boolean;
}

const ResetPwdModal = ({ isOpen }: ResetPwdModalProps) => {
  const search = useLocation().search;
  const params = queryString.parse(search, {
    parseNumbers: true,
    parseBooleans: true,
  });
  const currentEmail = useSnapshot(modalStore).currentEmail;
  const email = params.email ? decodeURIComponent(params.email as string) : '';
  const code = params.code ? decodeURIComponent(params.code as string) : '';

  const [emailState, setEmailState] = useState(email ?? currentEmail ?? '');
  const [codeState, setCodeState] = useState(code || '');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation function
  const validatePassword = (pwd: string) => {
    return {
      minLength: pwd.length >= 8,
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
    };
  };

  // Get password validation status
  const passwordValidation = validatePassword(newPwd);
  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const isFormValid =
    isPasswordValid && passwordsMatch && emailState && codeState && newPwd && confirmPwd;

  const [resetPassword, { loading: resetPasswordLoading }] = useMutation<
    IConfirmForgotPasswordMutation,
    IConfirmForgotPasswordMutationVariables
  >(RESET_PASSWORD, {
    onCompleted: () => {
      toast.success('Password reset successful');
      handleResetSuccess();
    },
    onError: error => {
      toast.error(`Something went wrong: ${error.message}`);
    },
  });

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pwd = e.target.value;
    setNewPwd(pwd);
    setPasswordsMatch(pwd === confirmPwd);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pwd = e.target.value;
    setConfirmPwd(pwd);
    setPasswordsMatch(newPwd === pwd);
  };

  const handleResetPassword = () => {
    if (!isPasswordValid) {
      toast.error('Password does not meet the requirements');
      return;
    }

    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    resetPassword({
      variables: {
        input: {
          email: emailState,
          code: codeState,
          newPassword: newPwd,
        },
      },
    });
  };

  const handleResetSuccess = () => {
    modalActions.closeResetPwdModal();
    modalActions.openSignInModal({ email: emailState });
  };

  return (
    <Modal isOpen={isOpen} onClose={modalActions.closeResetPwdModal}>
      <Container>
        <TextContainer>
          <Title>Reset your password</Title>
          <Subtitle>Enter your new password below.</Subtitle>
        </TextContainer>
        <FormGroup>
          <Label>Email</Label>
          <Input
            type="email"
            value={emailState}
            onChange={e => setEmailState(e.target.value)}
            autoComplete="chrome-off"
            data-lpignore="true" // LastPass ignore
            data-form-type="other" // Generic form type
          />
        </FormGroup>
        <FormGroup>
          <Label>Verification Code</Label>
          <Input
            value={codeState}
            onChange={e => setCodeState(e.target.value)}
            autoComplete="off"
            data-lpignore="true" // LastPass ignore
            data-form-type="other" // Generic form type
          />
        </FormGroup>

        <FormGroup>
          <Label>New Password</Label>
          <PasswordInputWrapper>
            <Input
              type={showNewPassword ? 'text' : 'password'}
              value={newPwd}
              onChange={handleNewPasswordChange}
              placeholder="Enter new password"
              autoComplete="chrome-off"
              data-lpignore="true" // LastPass ignore
              data-form-type="other" // Generic form type
            />
            <EyeIcon
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
            </EyeIcon>
          </PasswordInputWrapper>
          {/* Password Requirements */}
          {newPwd.length > 0 && (
            <PasswordRequirements>
              <RequirementRow>
                <RequirementIcon isValid={passwordValidation.minLength}>
                  {passwordValidation.minLength ? '✓' : '○'}
                </RequirementIcon>
                <RequirementText isValid={passwordValidation.minLength}>
                  At least 8 characters
                </RequirementText>
              </RequirementRow>
              <RequirementRow>
                <RequirementIcon isValid={passwordValidation.hasUppercase}>
                  {passwordValidation.hasUppercase ? '✓' : '○'}
                </RequirementIcon>
                <RequirementText isValid={passwordValidation.hasUppercase}>
                  At least 1 uppercase letter
                </RequirementText>
              </RequirementRow>
              <RequirementRow>
                <RequirementIcon isValid={passwordValidation.hasLowercase}>
                  {passwordValidation.hasLowercase ? '✓' : '○'}
                </RequirementIcon>
                <RequirementText isValid={passwordValidation.hasLowercase}>
                  At least 1 lowercase letter
                </RequirementText>
              </RequirementRow>
              <RequirementRow>
                <RequirementIcon isValid={passwordValidation.hasNumber}>
                  {passwordValidation.hasNumber ? '✓' : '○'}
                </RequirementIcon>
                <RequirementText isValid={passwordValidation.hasNumber}>
                  At least 1 number
                </RequirementText>
              </RequirementRow>
            </PasswordRequirements>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Confirm Password</Label>
          <PasswordInputWrapper>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPwd}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirm new password"
              autoComplete="off"
              data-lpignore="true" // LastPass ignore
              data-form-type="other" // Generic form type
            />
            <EyeIcon
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
            </EyeIcon>
          </PasswordInputWrapper>
          {!passwordsMatch && confirmPwd !== '' && (
            <ValidationMessage>Passwords do not match</ValidationMessage>
          )}
        </FormGroup>

        <Button
          text="Reset Password"
          onClick={handleResetPassword}
          buttonWidth="100%"
          isLoading={resetPasswordLoading}
          isDisabled={!isFormValid || resetPasswordLoading}
        />
      </Container>
    </Modal>
  );
};

export default ResetPwdModal;
